import { useState, useEffect } from 'react';
import TopNav from '../components/TopNav';
import { ordersApi, walletApi } from '../services/api';
import { useApp } from '../AppContext';

const VALIDATION_MOCK = {
  fileName: 'May_Batch_Candidates.xlsx', rows: 42,
  valid: 39, errors: 2, warnings: 1,
  errorRows: [
    { row: 7, name: 'Deepak Sharma', msg: 'Mobile number invalid (must be 10 digits)' },
    { row: 23, name: 'Anita Verma', msg: 'Package value "Prem" not recognised. Use: Standard / Standard Plus / Premium / Executive' },
  ],
  warningRows: [{ row: 35, name: 'Ravi Kumar', msg: 'Email address missing. Invite SMS will be sent only.' }],
  creditSummary: [
    { pkg: 'Standard', count: 12, amt: 8496 },
    { pkg: 'Standard Plus', count: 8, amt: 8024 },
    { pkg: 'Premium', count: 16, amt: 22656 },
    { pkg: 'Executive', count: 3, amt: 7080 },
  ],
  totalIncGst: 46256,
  walletBalance: 12450,
};

const VALIDATION_MOCK_T = {
  fileName: 'File Name', rows: 0,
  valid: 0, errors: 0, warnings: 0
};

export default function BulkUpload({ onNavigate }) {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [orderType, setOrderType] = useState('invite');
  const [validating, setValidating] = useState(false);
  const [placing, setPlacing] = useState(false);
  const { addToast, refreshWalletBalance } = useApp();

  const [clientId, setClientId] = useState('');
  const [projectList, setProjectList] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [packageList, setPackageList] = useState([]);
  const [packagesLoaded, setPackagesLoaded] = useState(false);
  const [packageId, setPackageId] = useState('');
  const [scopeMode, setScopeMode] = useState('pkg');
  const [dlErrors, setDlErrors] = useState({});
  const dlErrStyle = k => dlErrors[k] ? { borderColor: 'var(--rd)', boxShadow: '0 0 0 2px rgba(229,62,62,.15)' } : {};
  const [walletBalance, setWalletBalance] = useState(0);
  const [orderResult, setOrderResult] = useState(null);
  const [bulkDocs, setBulkDocs] = useState([]);
  const [excelDragOver, setExcelDragOver] = useState(false);
  const [docsDragOver, setDocsDragOver] = useState(false);

  useEffect(() => {
    walletApi.getWalletDetails()
      .then(res => { const bal = Array.isArray(res) ? res[0]?.current_balance : res?.current_balance; if (bal != null) setWalletBalance(bal); })
      .catch(() => {});
  }, []);
  const [bulkPrice, setBulkPrice] = useState(null);

  useEffect(() => {
    ordersApi.orderCandidate()
      .then(res => {
        if (res?.clientid) setClientId(res.clientid);
        if (res?.projectlist) setProjectList(res.projectlist);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (projectList.length === 1) {
      setProjectId(p => p === projectList[0].value ? p : projectList[0].value);
    }
  }, [projectList]);

  useEffect(() => {
    if (scopeMode === 'alc') {
      setPackageId('');
    }
  }, [scopeMode]);


  const hasPackages = !packagesLoaded || packageList.length > 0;

  async function handleDownloadTemplate() {
    if (!checkProjectPackageSelected('downloading the template')) return;
    setDlErrors({});
    try {
      const blob = await ordersApi.downloadSampleExcel(clientId, projectId, packageId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'CreateBulkProfiles.xls';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      addToast(e.message || 'Failed to download template', 'error');
    }
  }

  useEffect(() => {
    if (!projectId) { setPackageList([]); setPackageId(''); setBulkPrice(null); setPackagesLoaded(false); return; }
    setPackagesLoaded(false);
    ordersApi.getPackagesList(projectId)
      .then(res => setPackageList(Array.isArray(res) ? res : []))
      .catch(() => setPackageList([]))
      .finally(() => setPackagesLoaded(true));
    setPackageId('');
    setBulkPrice(null);
  }, [projectId]);


  function isValidExcel(file) {
    return /\.(xls|xlsx)$/i.test(file.name);
  }

  function scrollToValidationCard() {
    setTimeout(() => document.getElementById('bulk-validation-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  async function processExcelFile(file) {
    if (!file) return;
    if (!isValidExcel(file)) { addToast('Please upload an Excel file (.xls or .xlsx).', 'error'); return; }
    if (file.size > 5 * 1024 * 1024) { addToast('Excel file must be under 5 MB.', 'error'); return; }
    setUploadedFile(file);
    setValidationResult({ fileName: file.name, rows: 0, valid: 0, errors: 0, warnings: 0, errorRows: [], warningRows: [], creditSummary: [], totalIncGst: 0 });
    setValidating(true);
    setBulkPrice(null);
    try {
      const res = await ordersApi.getBulkOrderPrice(clientId, projectId, packageId || 0, file);
      if (res) {
        setValidationResult({
          fileName: res.fileName || file.name,
          rows: res.rows ?? res.candidatecount ?? 0,
          valid: res.valid ?? res.validcount ?? 0,
          errors: res.errors ?? res.errorcount ?? 0,
          warnings: res.warnings ?? res.warningcount ?? 0,
          errorRows: res.errorRows || [],
          warningRows: res.warningRows || [],
          creditSummary: res.creditSummary || [],
          totalIncGst: res.totalIncGst ?? res.subtotal ?? res.price ?? 0,
        });
        setBulkPrice(res);
      }
    } catch (e) {
      addToast(e.message || 'Failed to validate file', 'error');
    } finally { setValidating(false); }
  }

  function checkProjectPackageSelected(action = 'uploading candidate excel file') {
    const projectMissing = projectList.length > 1 && !projectId;
    const packageMissing = scopeMode === 'pkg' && hasPackages && !packageId;
    if (projectMissing || packageMissing) {
      const errs = {};
      if (projectMissing) errs.projectId = true;
      if (packageMissing) errs.packageId = true;
      setDlErrors(errs);
      addToast(projectMissing ? `Please select a project before ${action}.` : `Please select a package before ${action}.`, 'error');
      document.getElementById(projectMissing ? 'bulk-project' : 'bulk-package')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }
    return true;
  }

  function handleFileUpload(e) {
    const file = e.target.files[0];
    e.target.value = '';
    if (!checkProjectPackageSelected()) return;
    processExcelFile(file);
  }

  const BULK_DOC_EXTS = ['.xlsx', '.xls', '.doc', '.docx', '.pdf', '.zip', '.rar', '.png', '.jpg', '.jpeg'];
  function addBulkDocs(files) {
    const valid = [];
    files.forEach(f => {
      const ext = '.' + f.name.split('.').pop().toLowerCase();
      if (!BULK_DOC_EXTS.includes(ext)) { addToast(`${f.name}: File type not supported.`, 'error'); return; }
      if (f.size > 100 * 1024 * 1024) { addToast(`${f.name} exceeds the 100 MB size limit.`, 'error'); return; }
      valid.push(f);
    });
    setBulkDocs(prev => {
      const existing = new Set(prev.map(f => f.name + f.size));
      return [...prev, ...valid.filter(f => !existing.has(f.name + f.size))];
    });
  }

  function handleBulkDocsChange(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    addBulkDocs(files);
  }

  function removeBulkDoc(index) {
    setBulkDocs(prev => prev.filter((_, i) => i !== index));
  }

  function resetBulkUpload() {
    setUploadedFile(null);
    setBulkPrice(null);
    setBulkDocs([]);
    setDlErrors({});
  }

  async function handlePlaceOrders() {
    if (!checkProjectPackageSelected('placing orders')) return;
    if (!uploadedFile || !isValidExcel(uploadedFile)) { addToast('Please upload candidate excel file.', 'error'); return; }
    if (bulkTotal > walletBalance) { addToast('Insufficient wallet balance', 'error'); return; }
    setPlacing(true);
    setOrderResult(null);
    try {
      if (orderType === 'invite') {
        const fd = new FormData();
        fd.append('PostedFile', uploadedFile);
        fd.append('projectid', projectId || '0');
        fd.append('packageid', packageId || '0');
        fd.append('clientid', clientId || '');
        const res = await ordersApi.bulkCandidateOrder(fd);
        setOrderResult(res);
        setValidationResult(prev => ({
          ...prev,
          valid:       res?.successCount ?? res?.success?.length ?? 0,
          errors:      res?.errorCount   ?? res?.errors?.length  ?? 0,
          warnings:    res?.warningCount ?? res?.warnings?.length ?? 0,
          errorRows:   (res?.errors   || []).map(e => ({ row: e.sr, name: e.name, msg: e.message })),
          warningRows: (res?.warnings || []).map(w => ({ row: w.sr, name: w.name, msg: w.message })),
        }));
        scrollToValidationCard();
        refreshWalletBalance();
        walletApi.getWalletDetails().then(r => { const bal = Array.isArray(r) ? r[0]?.current_balance : r?.current_balance; if (bal != null) setWalletBalance(bal); }).catch(() => {});
        resetBulkUpload();
      } else {
        const fd = new FormData();
        fd.append('PostedFile', uploadedFile);
        fd.append('projectid', projectId || '0');
        fd.append('packageid', packageId || '0');
        fd.append('clientid', clientId || '');
        bulkDocs.forEach(f => fd.append('BulkFiles', f));
        const res = await ordersApi.bulkOrderReport(fd);
        setOrderResult(res);
        setValidationResult(prev => ({
          ...prev,
          valid:       res?.successCount ?? res?.success?.length ?? 0,
          errors:      res?.errorCount   ?? res?.errors?.length  ?? 0,
          warnings:    res?.warningCount ?? res?.warnings?.length ?? 0,
          errorRows:   (res?.errors   || []).map(e => ({ row: e.sr, name: e.name, msg: e.message })),
          warningRows: (res?.warnings || []).map(w => ({ row: w.sr, name: w.name, msg: w.message })),
        }));
        scrollToValidationCard();
        refreshWalletBalance();
        walletApi.getWalletDetails().then(r => { const bal = Array.isArray(r) ? r[0]?.current_balance : r?.current_balance; if (bal != null) setWalletBalance(bal); }).catch(() => {});
        resetBulkUpload();
      }
    } catch (e) { addToast(e.message || 'Failed to place orders', 'error'); }
    finally { setPlacing(false); }
  }

  const bulkTotal = !uploadedFile ? 0 : (bulkPrice !== null ? (Number(bulkPrice?.subtotal ?? bulkPrice?.price ?? bulkPrice) || 0) : (validationResult?.totalIncGst || 0));
  const insufficientBalance = !!uploadedFile && validationResult && bulkTotal > walletBalance;

  return (
    <>
      <TopNav currentPage="new-order" onNavigate={onNavigate} />
      <div className="pg">
        <div className="phd">
          <div><div className="ptitle">Bulk Order Upload</div><div className="psub">Upload multiple candidates at once via CSV / Excel template</div></div>
          <div className="flex g8">
            <button className="btn bout" onClick={handleDownloadTemplate}>⬇ Download Template (Excel)</button>
            <button className="btn bghost" onClick={() => onNavigate('orders')}>← Back to My Orders</button>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="otabs" style={{ maxWidth: 480 }}>
          <div className="otab" onClick={() => onNavigate('new-order-invite')}>📤 Invite Candidate</div>
          <div className="otab" onClick={() => onNavigate('new-order-direct')}>⚡ Direct Order</div>
          <div className="otab on">📊 Bulk Upload</div>
        </div>

        <div className="gr2" style={{ gap: '22px' }}>
          <div>
            {/* How it works */}
            <div className="card mb16">
              <div className="chd"><div className="ctitle">ℹ️ How Bulk Upload Works</div></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  { n: 1, title: 'Download our CSV / Excel template', desc: 'Template includes required columns: Name, DOB, Mobile, Email, Emp ID, Package, Address (for Direct orders)' },
                  { n: 2, title: 'Fill in candidate details & upload', desc: 'We validate each row — errors are highlighted with reasons before any orders are placed' },
                  { n: 3, title: 'Review, confirm & place all orders', desc: 'See credit summary before final confirmation. Orders are placed in batch instantly.' },
                ].map(s => (
                  <div key={s.n} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: 'var(--or)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 800, color: 'var(--nv)', flexShrink: 0 }}>{s.n}</div>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--nv)' }}>{s.title}</div>
                      <div style={{ fontSize: '11.5px', color: 'var(--g600)', marginTop: '2px' }}>{s.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Package vs A-la-carte — only relevant when there's a project to pin down */}
            {projectList.length > 1 && (
              <div className="card mb16">
                <div className="chd"><div className="ctitle">Order Scope</div></div>
                <div className="mode-toggle">
                  <div className={`mode-opt${scopeMode === 'pkg' ? ' on' : ''}`} onClick={() => setScopeMode('pkg')}>
                    <div className="mode-radio" />
                    <div>
                      <div className="mode-title">📦 Select Package</div>
                      <div className="mode-desc">Pick a project &amp; package for this batch</div>
                    </div>
                  </div>
                  <div className={`mode-opt${scopeMode === 'alc' ? ' on' : ''}`} onClick={() => setScopeMode('alc')}>
                    <div className="mode-radio" />
                    <div>
                      <div className="mode-title">🛒 A-la-carte</div>
                      <div className="mode-desc">Project &amp; package are set per row in the excel template</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Project & Package */}
            {((scopeMode === 'pkg' && hasPackages) || projectList.length > 1) && (
            <div className="card mb16">
              <div className="chd"><div className="ctitle">Project &amp; Package</div></div>
              <div className="fgrid">
                {projectList.length > 1 && (
                  <div className="fg full">
                    <label className="flbl">Project <span className="req">*</span></label>
                    <select id="bulk-project" className="sel" style={dlErrStyle('projectId')} value={projectId} onChange={e => { setProjectId(e.target.value); setDlErrors(ev => ({ ...ev, projectId: false })); }}>
                      <option value="">— Select a project —</option>
                      {projectList.map(p => (
                        <option key={p.value} value={p.value} disabled={p.disabled}>{p.text}</option>
                      ))}
                    </select>
                  </div>
                )}
                {scopeMode === 'pkg' && hasPackages && (
                  <div className="fg full">
                    <label className="flbl">BGV Package <span className="req">*</span></label>
                    <select id="bulk-package" className="sel" style={dlErrStyle('packageId')} value={packageId} onChange={e => { setPackageId(e.target.value); setDlErrors(ev => ({ ...ev, packageId: false })); }} disabled={!projectId || packageList.length === 0}>
                      <option value="">— Select a package —</option>
                      {packageList.map(p => (
                        <option key={p.value} value={p.value}>{p.text}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Bulk order type */}
            <div className="card mb16">
              <div className="chd"><div className="ctitle">Bulk Order Type</div></div>
              <div className="mode-toggle">
                <div className={`mode-opt${orderType === 'invite' ? ' on' : ''}`} onClick={() => setOrderType('invite')}>
                  <div className="mode-radio" />
                  <div>
                    <div className="mode-title">📤 Invite Candidates (Recommended)</div>
                    <div className="mode-desc">Invite links sent to all candidates via email. Credits deducted on submission. Auto-refund after 7 days.</div>
                  </div>
                </div>
                <div className={`mode-opt${orderType === 'direct' ? ' on' : ''}`} onClick={() => setOrderType('direct')}>
                  <div className="mode-radio" />
                  <div>
                    <div className="mode-title">⚡ Direct Orders (Docs pre-uploaded)</div>
                    <div className="mode-desc">Credits deducted immediately. Requires document column in template or separate zip upload.</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Upload zone */}
            <div className="card mb16">
              <div className="chd"><div className="ctitle">Upload Candidate Excel</div><div className="csub">Excel (.xls, .xlsx) only</div></div>
              <label
                className="upzone"
                style={{ display: 'block', padding: '10px', ...(excelDragOver ? { borderColor: 'var(--or)', background: 'var(--or-lt)', boxShadow: '0 0 0 2px rgba(245,166,35,.2)' } : {}) }}
                onDragOver={e => { e.preventDefault(); setExcelDragOver(true); }}
                onDragLeave={() => setExcelDragOver(false)}
                onDrop={e => { e.preventDefault(); setExcelDragOver(false); if (!checkProjectPackageSelected()) return; processExcelFile(e.dataTransfer.files[0]); }}
              >
                <input type="file" accept=".xlsx,.xls" onChange={handleFileUpload} style={{ display: 'none' }} />
                <div style={{ fontSize: '28px', marginBottom: '8px' }}>📂</div>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--nv)' }}><strong>Click to upload</strong> or drag &amp; drop your file here</div>
                <div style={{ fontSize: '11px', color: 'var(--g400)', marginTop: '4px' }}>Excel only (.xls, .xlsx) · Max 500 rows · Max 5 MB</div>
              </label>
              {(uploadedFile || validationResult) && (
                <div style={{ marginTop: '12px', padding: '12px 14px', background: 'var(--gr-lt)', borderRadius: 'var(--rs)', border: '1px solid rgba(13,179,107,.25)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '20px' }}>📊</span>
                    <div>
                      <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--nv)' }}>{uploadedFile?.name || validationResult.fileName}</div>
                      <div style={{ fontSize: '11px', color: 'var(--g400)' }}>{bulkPrice?.candidatecount ?? validationResult?.rows} candidates · {bulkPrice?.filesizemb ?? (uploadedFile ? Math.round(uploadedFile.size / 1024 / 1024 * 10) / 10 : '')} MB · Uploaded just now</div>
                    </div>
                  </div>
                  <span style={{ fontSize: '12px', color: 'var(--g400)', cursor: 'pointer' }} onClick={() => { setUploadedFile(null); setValidationResult(null); setBulkPrice(null); }}>✕</span>
                </div>
              )}
            </div>

            {/* Bulk documents — direct orders only */}
            {orderType === 'direct' && (
              <div className="card mb16">
                <div className="chd"><div className="ctitle">📎 Upload Documents</div><div className="csub">Direct orders only</div></div>
                <label
                  className="upzone"
                  style={{ display: 'block', padding: '10px', ...(docsDragOver ? { borderColor: 'var(--or)', background: 'var(--or-lt)', boxShadow: '0 0 0 2px rgba(245,166,35,.2)' } : {}) }}
                  onDragOver={e => { e.preventDefault(); setDocsDragOver(true); }}
                  onDragLeave={() => setDocsDragOver(false)}
                  onDrop={e => { e.preventDefault(); setDocsDragOver(false); addBulkDocs(Array.from(e.dataTransfer.files)); }}
                >
                  <input type="file" multiple accept=".xlsx,.xls,.doc,.docx,.pdf,.zip,.rar,.png,.jpg,.jpeg" onChange={handleBulkDocsChange} style={{ display: 'none' }} />
                  <div style={{ fontSize: '28px', marginBottom: '8px' }}>📂</div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--nv)' }}><strong>Click to upload</strong> or drag &amp; drop documents</div>
                  <div style={{ fontSize: '11px', color: 'var(--g400)', marginTop: '6px' }}>
                    .xlsx &nbsp;·&nbsp; .xls &nbsp;·&nbsp; .doc &nbsp;·&nbsp; .docx &nbsp;·&nbsp; .pdf &nbsp;·&nbsp; .zip &nbsp;·&nbsp; .rar &nbsp;·&nbsp; .png &nbsp;·&nbsp; .jpg &nbsp;·&nbsp; .jpeg &nbsp;·&nbsp; Upto 100 MB accepted
                  </div>
                </label>
                {bulkDocs.length > 0 && (
                  <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {bulkDocs.map((f, i) => (
                      <div key={i} style={{ padding: '9px 12px', background: 'var(--gr-lt)', borderRadius: 'var(--rs)', border: '1px solid rgba(13,179,107,.25)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontSize: '16px' }}>📄</span>
                          <div>
                            <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--nv)' }}>{f.name}</div>
                            <div style={{ fontSize: '10.5px', color: 'var(--g400)' }}>{Math.round(f.size / 1024 / 1024 * 100) / 100} MB</div>
                          </div>
                        </div>
                        <span style={{ fontSize: '12px', color: 'var(--g400)', cursor: 'pointer', padding: '4px 8px' }} onClick={() => removeBulkDoc(i)}>✕</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            
          </div>

          <div>
            {/* Validation results */}
            {validationResult && (
              <div className="card mb16" id="bulk-validation-card">
                <div className="chd">
                  <div><div className="ctitle">🔍 Validation Results</div><div className="csub">{validationResult.fileName} · {validationResult.rows} rows</div></div>
                  <span className="chip cg">{validationResult.valid} Valid</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '14px' }}>
                  <div style={{ textAlign: 'center', padding: '12px', background: 'var(--gr-lt)', borderRadius: 'var(--rs)', border: '1px solid rgba(13,179,107,.2)' }}>
                    <div style={{ fontSize: '20px', fontWeight: 800, color: 'var(--gr)' }}>{validationResult.valid}</div>
                    <div style={{ fontSize: '10.5px', color: 'var(--g600)', marginTop: '2px' }}>Valid rows</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '12px', background: 'var(--rd-lt)', borderRadius: 'var(--rs)', border: '1px solid rgba(229,62,62,.2)' }}>
                    <div style={{ fontSize: '20px', fontWeight: 800, color: 'var(--rd)' }}>{validationResult.errors}</div>
                    <div style={{ fontSize: '10.5px', color: 'var(--g600)', marginTop: '2px' }}>Errors</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '12px', background: 'var(--am-lt)', borderRadius: 'var(--rs)', border: '1px solid rgba(245,158,11,.2)' }}>
                    <div style={{ fontSize: '20px', fontWeight: 800, color: 'var(--am)' }}>{validationResult.warnings}</div>
                    <div style={{ fontSize: '10.5px', color: 'var(--g600)', marginTop: '2px' }}>Warning</div>
                  </div>
                </div>
                {(validationResult.errors > 0 || validationResult.errorRows?.length > 0) && (
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--rd)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '6px' }}>❌ Errors (must fix before placing orders)</div>
                    {(validationResult.errorRows || []).map((e, i) => (
                      <div key={i} style={{ padding: '9px 12px', background: 'var(--rd-lt)', borderRadius: '6px', borderLeft: '3px solid var(--rd)', marginBottom: '6px', fontSize: '11.5px', color: 'var(--g800)' }}>
                        <strong>Row {e.row} — {e.name}:</strong> {e.msg}
                      </div>
                    ))}
                  </div>
                )}
                {(validationResult.warnings > 0 || validationResult.warningRows?.length > 0) && (
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--am)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '6px' }}>⚠️ Warnings (can proceed, will skip these rows)</div>
                    {(validationResult.warningRows || []).map((w, i) => (
                      <div key={i} style={{ padding: '9px 12px', background: 'var(--am-lt)', borderRadius: '6px', borderLeft: '3px solid var(--am)', marginBottom: '6px', fontSize: '11.5px', color: 'var(--g800)' }}>
                        <strong>Row {w.row} — {w.name}:</strong> {w.msg}
                      </div>
                    ))}
                  </div>
                )}
                {orderResult?.success?.length > 0 && (
                  <div style={{ marginTop: '10px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--gr)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '6px' }}>✅ Successfully Placed</div>
                    {orderResult.success.map((s, i) => (
                      <div key={i} style={{ padding: '9px 12px', background: 'var(--gr-lt)', borderRadius: '6px', borderLeft: '3px solid var(--gr)', marginBottom: '6px', fontSize: '11.5px', color: 'var(--g800)' }}>
                        <strong>Sr. {s.sr} — {s.name}:</strong> {s.message}
                      </div>
                    ))}
                  </div>
                )}
                {/* {orderResult
                  ? <button className="btn bpri wf mt12" onClick={() => onNavigate('orders')}>View All Orders →</button>
                  : <button className="btn bout bsm mt12 wf">⬇ Download Error Report</button>
                } */}
              </div>
            )}

            {/* Credit summary */}
            {/* {validationResult && ( */}
            <div style={{ position: 'sticky', top: 80 }}>
              <div className="card mb16">
                <div className="chd"><div className="ctitle">💳 Credit Summary ({validationResult?.valid || '0'} valid orders)</div></div>
                <div className="amtbox">
                  {(validationResult?.creditSummary || []).map(c => (
                    <div key={c.pkg} className="amtrow"><span className="aml">{c.pkg} ({c.count} candidates)</span><span className="amv">₹{(c.amt || 0).toLocaleString('en-IN')}</span></div>
                  ))}
                  <div className="amtrow"><span className="amtl">Total</span><span className="amtv">₹{bulkTotal.toLocaleString('en-IN')}</span></div>
                </div>
                <hr className="hr" />
                <div className="flex jb aic mb8 ts"><span className="tg">Current wallet balance</span><span className="f7 t-gr">₹{walletBalance.toLocaleString('en-IN')}</span></div>
                <div className="flex jb aic mb8 ts"><span className="tg">Balance after batch</span><span className={`f7 ${insufficientBalance ? 't-rd' : 't-gr'}`}>₹{(walletBalance - bulkTotal).toLocaleString('en-IN')}</span></div>
                {insufficientBalance && (
                  <div className="alert a-rd mt8"><div className="aico">🔴</div><div className="abody"><div className="atitle">Insufficient credits</div>Your wallet balance is insufficient for this batch. Please add at least ₹{(bulkTotal - walletBalance + 1000).toLocaleString('en-IN')} before placing these orders.</div></div>
                )}
                {insufficientBalance && <button className="btn bpri wf mt12" onClick={() => onNavigate('wallet')}>+ Add Credits First →</button>}
              </div>
            {/*)}*/}

            <div className="alert a-bl mb16"><div className="aico">ℹ️</div><div className="abody" style={{ fontSize: '11.5px' }}>By placing bulk orders you confirm that written consent has been obtained from all candidates as required by applicable law.</div></div>

            <button
              className="btn bsec wf blg"
              style={validationResult?.errors > 0 || insufficientBalance ? { opacity: .5, cursor: 'not-allowed' } : {}}
              onClick={handlePlaceOrders}
              disabled={validationResult?.errors > 0 || insufficientBalance || placing}
            >
              {placing ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Placing orders…</> : `📊 Place ${validationResult?.valid || 0} Orders${validationResult?.errors > 0 ? ' (fix errors first)' : ''}`}
            </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
